<script>
import { CONSTANT } from '../../../utils/constant.js'
import ConfigurationService from '../configuration.service'
import { mapGetters } from 'vuex'
import { EventBus } from '../../../event-bus.js'
import sharedService from '../../../utils/shared-service'
import NationalIdentifier from './national-identifier/national-identifier'
import BillingAddress from './billing-address/billing-address'
import ComplianceSettings from './compliance-settings/compliance-settings'
import DcfVariance from '@/components/configuration/checkout/dcf-variance/dcf-variance'

const configurationService = new ConfigurationService()

export default {

  components: {
    DcfVariance,
    NationalIdentifier,
    BillingAddress,
    ComplianceSettings
  },

  data () {
    return {
      CONSTANT: CONSTANT,
      configurationService: configurationService,
      errorReason: '',
      item: '',
      dpaIdsBySrciEntityId: [],
      isMPTEnabled: false
    }
  },

  computed: {
    shippingCountryCodes: {
      get () {
        return this.$store.state.configuration.checkout.shippingCountryCodes
      },
      set (value) {
        this.$store.commit('updateShippingCountryCodes', value)
        sharedService.updateCheckoutButton(false)
      }
    },

    shippingAddress: {
      get () {
        return this.$store.state.configuration.checkout.shippingAddress
      },
      set (value) {
        this.$store.commit('updateShippingAddress', value)
        sharedService.updateCheckoutButton(false)
      }
    },

    dsaTransactionOptions: {
      get () {
        return this.$store.state.configuration.checkout.dsaTransactionOptions
      }
    },

    srciEntityId: {
      get () {
        return this.$store.state.configuration.checkout.srcId || ''
      },
      set (value) {
        this.$store.commit('updateSrcEntityId', value)
        sharedService.updateCheckoutButton(false)
      }
    },

    srciDpaId: {
      get () {
        return this.$store.state.configuration.checkout.dpaId || ''
      },
      set (value) {
        this.$store.commit('updateDpaId', value)
        sharedService.updateCheckoutButton(false)
      }
    },

    payloadTypeIndicatorCheckout: {
      get () {
        return this.$store.state.configuration.checkout.payloadTypeIndicatorCheckout
      },
      set (value) {
        this.$store.commit('updatePayloadTypeIndicatorCheckout', value)
        sharedService.updateCheckoutButton(false)
      }
    },

    payloadTypeIndicatorPayload: {
      get () {
        return this.$store.state.configuration.checkout.payloadTypeIndicatorPayload
      },
      set (value) {
        this.$store.commit('updatePayloadTypeIndicatorPayload', value)
        sharedService.updateCheckoutButton(false)
      }
    },

    confirmPayment: {
      get () {
        return this.$store.state.configuration.checkout.confirmPayment || false
      },
      set (value) {
        this.$store.commit('updateConfirmPayment', value)
      }
    },

    // A getter and setter for the checkoutType property.
    checkoutType: {
      get () {
        return this.$store.state.configuration.checkout.checkoutType || 'NONE'
      },
      set (value) {
        sharedService.updateSelecteNonPaymentSrci(value)
        this.$store.dispatch('updateCheckoutType', value)
      }
    },

    // A getter and setter for the threeDSPreference property.
    threeDSPreference: {
      get () {
        return this.$store.getters.threeDSPreference || null
      },
      set (value) {
        this.$store.dispatch('updateThreeDSPreference', value)
      }
    },

    manualPANTokenization: {
      get () {
        return this.$store.getters.enableMPT
      },
      set (value) {
        this.$store.dispatch('updateMPTFlow', value)
        if (!value) {
          this.$store.dispatch('updateProfileOptOut', value)
        }
      }
    },

    ...mapGetters(['srcEntityIds', 'dpaIds', 'isSRCCheckoutDisabled', 'nonPaymentSrci', 'selectedNonPaymentSrci', 'toggleMacsFeaturesList'])
  },
  watch: {
    toggleMacsFeaturesList () {
      this.isMPTEnabled = sharedService.checkFeatureFlag('START_MPT_CHANGES')
      if (!this.isMPTEnabled) {
        this.$store.dispatch('updateMPTFlow', this.isMPTEnabled)
      }
    }
  },

  created () {
    this.initialSRCConfiguration()
    this.isMPTEnabled = sharedService.checkFeatureFlag('START_MPT_CHANGES')
  },

  mounted () {
    EventBus.$on('refreshSrcId', () => {
      this.changeSrcCredentialBasedOnEnv()
    })
  },

  methods: {

    /**
     * This method sets the initial src id and dpa id and should call only once
     */
    initialSRCConfiguration () {
      if (this.srcEntityIds) {
        if (!this.srciEntityId) {
          this.srciEntityId = this.srcEntityIds[0]
        }
        this.dpaIdsBySrciEntityId = this.srciEntityId.dpaConfigs
        if (!this.srciDpaId) {
          this.srciDpaId = this.dpaIdsBySrciEntityId[0]
        }
      }
    },

    /**
     * Set New SRC Credentials After Api Load
     */
    setSrciCredentials () {
      this.srciEntityId = this.srcEntityIds[0]
      this.dpaIdsBySrciEntityId = this.srciEntityId.dpaConfigs
      this.srciDpaId = this.dpaIdsBySrciEntityId[0]
    },

    /**
     * This method update the dpa id
     * @param dpaID
     */
    updateDpaId (selectedDpaId) {
      this.srciDpaId = selectedDpaId
    },

    /**
     * This method update the srciEntityId
     * @param dpaID
     */
    updateSrciEntityId (selectedSrcEntity) {
      this.dpaIdsBySrciEntityId = selectedSrcEntity.dpaConfigs
      this.srciDpaId = this.dpaIdsBySrciEntityId[0]
    },

    /**
     * Loads src credentials based on environment change
     */
    changeSrcCredentialBasedOnEnv () {
      if (this.srcEntityIds) {
        if (this.srcEntityIds[0].displayName) {
          this.setSrciCredentials()
        }
      }
    },

    /**
     * This method toggle the customer preference based on flag
     * @param key
     * @param $event
     */
    toggleConsumerProfilePreference (key, $event) {
      this.dsaTransactionOptions[key] = $event.target.checked
      this.$store.commit(
        'updateDSATransactionOptions',
        this.dsaTransactionOptions
      )
      sharedService.updateCheckoutButton(false)
    }
  }
}
</script>

<template>
  <div id="configuration-checkout">
    <div class="card mb-4">
      <div
        v-if="errorReason"
        class="alert alert-danger"
        role="alert"
      >
        {{ $t('errorMessages.' + errorReason) }}
      </div>
      <h5
        id="headingSrciCredential"
        aria-controls="collapseSrciCredential"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#collapseSrciCredential"
        data-test="srci-credential-collapse"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t ('header.srciCredentials') }}</span>
        <span class="collapsible-arrow pull-right" />
      </h5>
      <div
        id="collapseSrciCredential"
        aria-labelledby="headingSrciCredential"
        class="card-body collapse show"
      >
        <div
          id="SRCI-entity-ID"
          class="form-group"
          data-test="SRCI-entity-ID-container"
        >
          <label>{{ $t ('label.srciEntityId') }}</label>
          <select
            id="select-SRCI-entity-ID"
            v-model="srciEntityId"
            class="option-dpa form-control"
            data-test="select-SRCI-entity-ID"
            @change="updateSrciEntityId(srciEntityId)"
          >
            <option
              v-for="(entity) in srcEntityIds"
              :key="entity.displayName"
              :value="entity"
            >
              {{ entity.displayName }}
            </option>
          </select>
        </div>
        <div
          id="dpa-id"
          class="form-group"
        >
          <label>{{ $t ('label.DpaIds') }}</label>
          <select
            id="select-dpa-id"
            v-model="srciDpaId"
            class="option-dpa form-control"
            data-test="select-dpa-id"
            @change="updateDpaId(srciDpaId)"
          >
            <option
              v-for="dpa in dpaIdsBySrciEntityId"
              :key="dpa.displayDpaName"
              :value="dpa"
            >
              {{ dpa.displayDpaName }}
            </option>
          </select>

          <div
            v-if="srciEntityId.srci && srciDpaId.dpa"
            id="origin-url"
            class="mt-4"
          >
            <label class="font-weight-bold mb-1">{{ $t ('label.srcInitiatorId') }}</label>
            <p>{{ ((srciEntityId || {}).srci || [])[0].srcInitiatorId || ' ' }}</p>

            <label class="font-weight-bold mb-1">{{ $t ('label.srciDpaId') }}</label>
            <p>{{ ((srciDpaId || {}).dpa || [])[0].dpaId || ' ' }}</p>
            <template v-if="srciEntityId.srci.length > 1">
              <label class="font-weight-bold mb-1">{{ $t ('label.srcInitiatorIdVisa') }}</label>
              <p>{{ ((srciEntityId || {}).srci || [])[1].srcInitiatorId || ' ' }}</p>
            </template>
            <template v-if="srciDpaId.dpa.length > 1">
              <label class="font-weight-bold mb-1">{{ $t ('label.srciDpaIdVisa') }}</label>
              <p>{{ ((srciDpaId || {}).dpa || [])[1].dpaId || ' ' }}</p>
            </template>

            <template v-if="srciEntityId.srci.length > 1">
              <label class="font-weight-bold mb-1">{{ $t ('label.srcInitiatorIdAmex') }}</label>
              <p>{{ ((srciEntityId || {}).srci || [])[2].srcInitiatorId || ' ' }}</p>
            </template>
            <template v-if="srciDpaId.dpa.length > 1">
              <label class="font-weight-bold mb-1">{{ $t ('label.srciDpaIdAmex') }}</label>
              <p>{{ ((srciDpaId || {}).dpa || [])[2].dpaId || ' ' }}</p>
            </template>
            <template v-if="srciEntityId.srci.length > 1">
              <label class="font-weight-bold mb-1">{{ $t ('label.srcInitiatorIdDiscover') }}</label>
              <p>{{ ((srciEntityId || {}).srci || [])[3].srcInitiatorId || ' ' }}</p>
            </template>
            <template v-if="srciDpaId.dpa.length > 1">
              <label class="font-weight-bold mb-1">{{ $t ('label.srciDpaIdDiscover') }}</label>
              <p>{{ ((srciDpaId || {}).dpa || [])[3].dpaId || ' ' }}</p>
            </template>

          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isMPTEnabled"
      class="card mb-4">
      <h5
        id="headingMpt"
        aria-controls="collapseMpt"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#collapseMpt"
        data-test="mpt-collapse"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{
          $t("header.MPTHeading")
        }}</span>
        <span class="collapsible-arrow pull-right" />
      </h5>

      <div
        id="collapseMpt"
        aria-labelledby="headingMpt"
        class="card-body collapse"
      >
        <div class="form-check form-check-inline">
          <input
            id="mpt"
            :checked="manualPANTokenization"
            v-model="manualPANTokenization"
            class="form-check-input"
            type="checkbox"
          >
          <label
            class="form-check-label"
          >{{ $t("label.enableMPT") }}
          </label>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <h5
        id="headingConsumerProfilePreference"
        aria-controls="collapseConsumerProfilePreference"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#collapseConsumerProfilePreference"
        data-test="consumer-profile-preference-collapse"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t ('header.consumerProfilePreference') }}</span>
        <span class="collapsible-arrow pull-right" />
      </h5>

      <div
        id="collapseConsumerProfilePreference"
        aria-labelledby="headingConsumerProfilePreference"
        class="card-body collapse"
      >
        <label>{{ $t ('label.selectConsumerProfilePreference') }}</label>
        <div id="dsa-transaction-options">
          <div
            v-for="(key,value) in CONSTANT.CONFIG.CONSUMER_PROFILE_PREFERENCES"
            :key="key"
            class="form-check form-check-inline"
          >
            <input
              :id="'inlineCheckbox' + key"
              :checked="dsaTransactionOptions[key]"
              :data-test="'checkbox-' + key"
              class="form-check-input"
              type="checkbox"
              @click="toggleConsumerProfilePreference(key, $event)"
            >
            <label
              :for="'inlineCheckbox' + key"
              class="form-check-label"
            >{{ value }}</label>
          </div>
        </div>

        <!-- Confirm Payment option -->
        <label class="mt-3">{{ $t ('label.confirmPayment') }}</label>
        <div id="confirmPayment">
          <div
            v-for="(key, value) in CONSTANT.CONFIG.CONFIRM_PAYMENT_OPTIONS"
            :key="value"
            class="form-check form-check-inline"
          >
            <input
              :id="'inlineRadio' + key"
              v-model="confirmPayment"
              :data-test="'radio-' + key"
              :value="key"
              name="confirmPayment"
              class="form-check-input"
              type="radio"
            >
            <label
              :for="'inlineRadio' + key"
              class="form-check-label"
            >
              {{ value }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="card mb-4">
      <h5
        id="headingCheckoutType"
        aria-controls="collapseCheckoutType"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#collapseCheckoutType"
        data-test="checkout-type-collapse"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t ('header.checkoutType') }}</span>
        <span class="collapsible-arrow pull-right" />
      </h5>

      <div
        id="collapseCheckoutType"
        aria-labelledby="headingCheckoutType"
        class="card-body collapse"
      >
        <label class="mt-3">{{ $t ('label.checkoutTypeLabel') }}</label>
        <div id="checkoutType">
          <div
            v-for="(key, value) in CONSTANT.CONFIG.CHECKOUT_TYPE_OPTIONS"
            :key="value"
            class="form-check form-check-inline"
          >
            <input
              :id="'inlineRadio' + key"
              v-model="checkoutType"
              :data-test="'radio-' + key"
              :value="key"
              name="checkoutType"
              class="form-check-input"
              type="radio"
            >
            <label
              :for="'inlineRadio' + key"
              class="form-check-label"
            >
              {{ value }}
            </label>
          </div>
        </div>

        <label class="mt-3">{{ $t ('label.nonPaymentSrci') }}</label>
        <div id="nonPaymentSrci">
          <select
            id="select-nonPayment-SRCI-entity-ID"
            v-model="selectedNonPaymentSrci"
            class="option-dpa form-control"
            data-test="select-nonPayment-SRCI-entity-ID"
          >
            <option value="null">{{ $t ('label.nonPaymentSrci') }}</option>
            <option
              v-for="entity in nonPaymentSrci"
              :key="entity.displayName"
              :value="entity.srci[0].srcInitiatorId"
            >
              {{ entity.displayName }}
            </option>
          </select>
        </div>
        <div
          v-if="selectedNonPaymentSrci"
          id="nonPayment-srci"
          class="mt-4"
        >
          <label class="font-weight-bold mb-1">{{ $t ('label.nonPatmentSrcInitiatorId') }}</label>
          <p>{{ selectedNonPaymentSrci }}</p>
        </div>
      </div>
    </div>
    <div class="card mb-4">
      <h5
        id="heading3DSPreference"
        aria-controls="collapse3DSPreference"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#collapse3DSPreference"
        data-test="3DS-preference-collapse"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t ('header.threeDSPreferenceHeading') }}</span>
        <span class="collapsible-arrow pull-right" />
      </h5>

      <div
        id="collapse3DSPreference"
        aria-labelledby="heading3DSPreference"
        class="card-body collapse"
      >
        <label class="mt-3">{{ $t ('label.threeDSPreferenceLabel') }}</label>
        <div id="3DSPreference">
          <div
            v-for="(key, value) in CONSTANT.CONFIG.THREE_DS_PREFERENCE_OPTIONS"
            :key="value"
            class="form-check form-check-inline"
          >
            <input
              :id="'inlineRadio' + key"
              v-model="threeDSPreference"
              :data-test="'radio-' + key"
              :value="key"
              name="threeDSPreference"
              class="form-check-input"
              type="radio"
            >
            <label
              :for="'inlineRadio' + key"
              class="form-check-label"
            >
              {{ value }}
            </label>
          </div>
        </div>
      </div>
    </div>
    <dcf-variance />
    <div class="card mb-4">
      <h5
        id="headingPayloadRetrievalPreference"
        aria-controls="collapsePayloadRetrievalPreference"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#collapsePayloadRetrievalPreference"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t ('header.payloadRetrievalPreference') }}</span>
        <span class="collapsible-arrow pull-right" />
      </h5>

      <div
        id="collapsePayloadRetrievalPreference"
        aria-labelledby="headingPayloadRetrievalPreference"
        class="card-body collapse"
      >
        <label>{{ $t ('label.selectPayloadRetrievalType') }}</label>
        <div id="payload-retrieval-preference">
          <div class="row">
            <div class="col-sm-6 col-md-6 col-lg-6  mt-2">
              <label>{{ $t ('label.checkoutIndicator') }}:</label>
            </div>
            <div class="col-sm-6 col-lg-6 col-md-6 mt-1">
              <select
                id="payloadTypeIndicatorCheckout"
                v-model="payloadTypeIndicatorCheckout"
                :data-selected="payloadTypeIndicatorCheckout"
                class="form-control"
                required=""
              >
                <option
                  v-for="(checkoutType, key) in CONSTANT.CONFIG.PAYLOAD_RETRIEVAL_TYPES"
                  :key="checkoutType"
                  :value="key"
                >
                  {{ checkoutType }}
                </option>
              </select>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 col-md-6 col-lg-6  mt-3">
              <label>{{ $t ('label.payloadIndicator') }}:</label>
            </div>
            <div class="col-sm-6 col-md-6 col-lg-6 mt-1">
              <select
                id="payloadTypeIndicatorPayload"
                v-model="payloadTypeIndicatorPayload"
                :data-selected="payloadTypeIndicatorPayload"
                class="form-control"
                required=""
              >
                <option
                  v-for="(payloadType, key) in CONSTANT.CONFIG.PAYLOAD_RETRIEVAL_TYPES"
                  :key="payloadType"
                  :value="key"
                >
                  {{ payloadType }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <H5
        id="headingShippingAddress"
        aria-controls="collapseShippingAddress"
        aria-expanded="true"
        class="card-header collapsed cursor-pointer"
        data-target="#collapseShippingAddress"
        data-toggle="collapse"
      >
        <span class="font-weight-bold">{{ $t ('header.shippingAddressPreference') }}</span>
        <span class="collapsible-arrow pull-right" />
      </H5>
      <div
        id="collapseShippingAddress"
        aria-labelledby="headingShippingAddress"
        class="card-body collapse"
      >
        <label>{{ $t ('label.chooseShippingAddressPreference') }}</label>
        <div
          id="shipping-address-preference"
        >
          <div
            v-for="(key, value) in CONSTANT.CONFIG.SHIPPING_ADDRESS"
            :key="value"
            class="form-check form-check-inline"
          >
            <input
              :id="'inlineRadio' + key"
              v-model="shippingAddress"
              :data-test="'radio-' + key"
              :value="key"
              name="shippingAddressSelection"
              class="form-check-input"
              type="radio"
            >
            <label
              :for="'inlineRadio' + key"
              class="form-check-label"
            >
              {{ value }}
            </label>
          </div>
          <hr class="my-4">
          <div class="form-group">
            <label><strong>{{ $t ('label.acceptedShippingCountriesCode') }}</strong></label>
            <p
              id="shipping-profiles"
            >
              {{ $t('label.acceptedShippingCountriesCodeDescription') }}
            </p>
            <input
              id="input-shipping-profiles"
              v-model="shippingCountryCodes"
              class="form-control"
              data-test="input-shipping-profiles"
              type="text"
            >
          </div>
        </div>
      </div>
    </div>

    <billing-address />

    <national-identifier/>
    <compliance-settings />
  </div>
</template>
